import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { XMarkIcon } from "@heroicons/react/20/solid";
import { AppDispatch, RootState } from "../../../store/store";
import { STATUS } from "../../../store/slices/OrganizationSlice";
import {
  OrganizationV3,
  RenameResult,
  resetRenameState,
} from "../../../store/slices/OrganizationsV3Slice";
import { renameOrganizationAsync } from "../../../store/thunks/OrganizationsV3Thunk";
import { ORG_NAME_MAX_LENGTH } from "../../../constants/organizations";
import { Dialog, DialogActions, DialogBody, DialogTitle } from "../../Catalyst/dialog";
import { Field } from "../../Catalyst/fieldset";
import { Input } from "../../Catalyst/input";
import { Button } from "../../Catalyst/button";
import { useToast } from "../../../contexts/ToastContext";

const ButtonSpinner = () => (
  <svg className="h-4 w-4 animate-spin" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
    <path
      className="opacity-75"
      fill="currentColor"
      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
    />
  </svg>
);

interface RenameOrgDialogProps {
  organization: OrganizationV3 | null;
  open: boolean;
  onClose: () => void;
}

// ADM-12: rename an organization from the list. On success the slice updates the
// row in place, so the list reflects the new name without a refetch.
const RenameOrgDialog = ({ organization, open, onClose }: RenameOrgDialogProps) => {
  const dispatch = useDispatch<AppDispatch>();
  const { showSuccessToast } = useToast();
  const { renameStatus, renameError } = useSelector(
    (state: RootState) => state.OrganizationsV3Store
  );

  const [name, setName] = useState("");

  const isSaving = renameStatus === STATUS.LOADING;
  const trimmedName = name.trim();
  // Save stays disabled while the field is empty or unchanged (per the ticket).
  const canSave =
    !isSaving && trimmedName.length > 0 && trimmedName !== (organization?.name ?? "").trim();

  // Pre-populate with the current name and clear stale errors each time it opens.
  useEffect(() => {
    if (open && organization) {
      dispatch(resetRenameState());
      setName(organization.name);
    }
  }, [open, organization, dispatch]);

  const handleSave = async () => {
    if (!organization || !canSave) return;
    const result = await dispatch(
      renameOrganizationAsync({ orgId: organization.id, body: { name: trimmedName } })
    );
    if (!renameOrganizationAsync.fulfilled.match(result)) {
      // renameError is rendered inline; keep the dialog open for a retry.
      return;
    }
    const outcome = result.payload as RenameResult | undefined;
    // renamed=false means the server saw no change (Save is already disabled for
    // an unchanged name, this is the server's belt-and-braces); nothing to announce.
    if (outcome?.renamed !== false) {
      showSuccessToast(
        "Organization renamed",
        `Renamed ${outcome?.previousName ?? organization.name} to ${outcome?.name ?? trimmedName}.`
      );
    }
    onClose();
  };

  const handleNameChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setName(event.target.value);
    // A previous attempt's error is stale once the user starts editing again.
    if (renameError) dispatch(resetRenameState());
  };

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    handleSave();
  };

  return (
    // Ignore backdrop/Escape while saving so the dialog cannot vanish mid-request.
    <Dialog open={open} onClose={isSaving ? () => undefined : onClose}>
      <form onSubmit={handleSubmit}>
        <div className="flex items-start justify-between gap-4">
          <DialogTitle>Rename organization</DialogTitle>
          <button
            type="button"
            onClick={onClose}
            disabled={isSaving}
            aria-label="Close"
            className="shrink-0 rounded-md p-1 text-zinc-500 hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-50"
          >
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>
        <DialogBody>
          <Field>
            <Input
              name="organizationName"
              value={name}
              onChange={handleNameChange}
              maxLength={ORG_NAME_MAX_LENGTH}
              disabled={isSaving}
              autoFocus
              aria-label="Organization name"
              aria-describedby="rename-org-counter"
            />
          </Field>
          <p id="rename-org-counter" className="mt-2 text-right text-sm text-textColor">
            {name.length}/{ORG_NAME_MAX_LENGTH} Character
          </p>
          {renameError && (
            <p className="mt-3 text-sm font-medium text-red-500">{renameError}</p>
          )}
        </DialogBody>
        <DialogActions>
          <Button outline onClick={onClose} disabled={isSaving}>
            Cancel
          </Button>
          <Button type="submit" color="green" disabled={!canSave}>
            {isSaving && <ButtonSpinner />}
            {isSaving ? "Saving..." : "Save"}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default RenameOrgDialog;
